import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Copy, Check, Loader2, ArrowLeft, Sparkles, Info } from "lucide-react";
import { toast } from "sonner";
import {
  RESULT_TYPES,
  RESULT_META,
  buildInitialBlocks,
  buildBlockText,
  computeWeights,
  buildFullMessage,
  loadWizardDraft,
} from "@/lib/resultBuilder";
import { DEMO_RESULT_BLOCKS } from "@/lib/demoData";

function timeAgo(date) {
  if (!date) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return `vor ${seconds} Sekunden`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `vor ${minutes} Min`;
  const hours = Math.floor(minutes / 60);
  return `vor ${hours} Std`;
}

export default function CoCreatorResult() {
  const navigate = useNavigate();
  const location = useLocation();

  const initialData = useMemo(
    () => location.state?.wizardData || loadWizardDraft() || {},
    [location.state]
  );
  const isDemo = Boolean(location.state?.demo);

  const [blocks, setBlocks] = useState(() =>
    isDemo ? { ...DEMO_RESULT_BLOCKS } : buildInitialBlocks(initialData)
  );
  const [updatedAt, setUpdatedAt] = useState(() => new Date());
  const [_tick, setTick] = useState(0);
  const [iteratingType, setIteratingType] = useState(null);
  const [copied, setCopied] = useState(false);

  // Refresh "vor X Sekunden" every 5s
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  const weights = useMemo(() => computeWeights(blocks), [blocks]);

  const handleIterate = (type) => {
    if (iteratingType) return;
    setIteratingType(type);
    setTimeout(() => {
      const original = isDemo
        ? DEMO_RESULT_BLOCKS[type]
        : buildBlockText(type, initialData);
      const meta = RESULT_META[type];
      const emphasised =
        `[${meta.name}-Fokus verstärkt] ` +
        original +
        `\n\nDieser Baustein wurde mit stärkerem ${meta.name}-Fokus neu erstellt — ` +
        `mehr ${
          type === "cat"
            ? "Wärme, Optionen und Wahlfreiheit"
            : type === "owl"
            ? "Kontext, Daten und Sinn"
            : type === "bee"
            ? "konkrete Schritte und Umsetzungstiefe"
            : "Klarheit zu Konsequenzen und nächstem Schritt"
        }.`;
      setBlocks((prev) => ({ ...prev, [type]: emphasised }));
      setUpdatedAt(new Date());
      setIteratingType(null);
    }, 1500);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildFullMessage(blocks));
      setCopied(true);
      toast.success("In die Zwischenablage kopiert");
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  return (
    <div
      className="min-h-screen bg-white flex flex-col"
      data-testid="co-creator-result-page"
    >
      <Header />

      <main className="flex-1 px-6 lg:px-8 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto relative">
          {isDemo && (
            <div
              data-testid="demo-mode-badge"
              className="absolute top-0 right-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-[#1E40AF] text-[11px] font-bold uppercase tracking-widest shadow-sm"
            >
              <Sparkles className="w-3 h-3" />
              Demo-Modus
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-[1.85fr_1fr] gap-8 lg:gap-12">
            {/* LEFT — Structured message */}
            <section data-testid="result-left-column">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-[#1E40AF] text-xs font-bold uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5" />
                Co-Creator Ergebnis
              </div>
              <h1
                className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 leading-tight"
                data-testid="cc-result-headline"
              >
                Deine Nachricht — strukturiert nach der 4FT-Formel
              </h1>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-2xl">
                Jeder Baustein wurde nach den Antworten aus dem Wizard erstellt.
              </p>

              <div className="mt-10 space-y-5">
                {RESULT_TYPES.map((type) => {
                  const meta = RESULT_META[type];
                  const text = blocks[type];
                  const isBusy = iteratingType === type;
                  return (
                    <article
                      key={type}
                      data-testid={`result-block-${type}`}
                      className={`relative overflow-hidden rounded-2xl border ${meta.border} pl-1.5 shadow-[0_2px_20px_-8px_rgba(15,23,42,0.08)] transition-all duration-300`}
                      style={{ backgroundColor: meta.bg }}
                    >
                      <div
                        className={`absolute left-0 top-0 bottom-0 w-1.5 ${meta.barClass}`}
                        aria-hidden="true"
                      />
                      <div className="pl-6 pr-6 sm:pl-8 sm:pr-8 py-6 sm:py-7">
                        <div className="flex items-center justify-between gap-3">
                          <span
                            className="text-xs font-bold uppercase tracking-widest text-slate-700"
                            data-testid={`result-block-label-${type}`}
                          >
                            {meta.label}
                          </span>
                          {isBusy && (
                            <span
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500"
                              data-testid={`result-block-busy-${type}`}
                            >
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              wird neu erstellt …
                            </span>
                          )}
                        </div>
                        <p
                          className={`mt-3 text-base sm:text-[17px] leading-relaxed whitespace-pre-line text-slate-800 transition-opacity duration-200 ${
                            isBusy ? "opacity-40" : "opacity-100"
                          }`}
                          data-testid={`result-block-text-${type}`}
                        >
                          {text}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>

              <p
                className="mt-6 text-sm text-slate-500"
                data-testid="result-updated-at"
              >
                Letzte Aktualisierung: {timeAgo(updatedAt)}
              </p>

              <div className="mt-10">
                <Button
                  onClick={() => navigate("/co-creator")}
                  variant="ghost"
                  data-testid="result-back-to-wizard"
                  className="rounded-xl text-slate-600 hover:text-slate-900 font-semibold"
                >
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  Zurück zum Wizard
                </Button>
              </div>
            </section>

            {/* RIGHT — Sidebar */}
            <aside
              className="space-y-6 lg:sticky lg:top-24 self-start"
              data-testid="result-right-column"
            >
              {/* Learning impulse */}
              <div
                className="bg-slate-50 border border-slate-200 rounded-2xl p-6"
                data-testid="learning-impulse"
              >
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-slate-500" />
                  <h2 className="text-sm font-bold uppercase tracking-widest text-slate-600">
                    Warum funktioniert diese Nachricht?
                  </h2>
                </div>
                <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                  Diese Nachricht beginnt mit der Katze, weil sie als Erstes
                  emotional aussteigt. Erst danach kommen Eule (Logik), Biene
                  (Umsetzung) und Hund (Konsequenz). Die Reihenfolge ist nicht
                  die Reihenfolge deiner Stärke — sondern die Reihenfolge, in
                  der der Empfänger verarbeitet.
                </p>
              </div>

              {/* Distribution */}
              <div
                className="bg-white dark:bg-slate-900 border border-slate-100 rounded-2xl p-6 shadow-[0_2px_20px_-12px_rgba(15,23,42,0.1)]"
                data-testid="distribution-chart"
              >
                <h2 className="text-sm font-bold uppercase tracking-widest text-slate-600">
                  Verteilung der Bausteine
                </h2>
                <div className="mt-4 space-y-3.5">
                  {RESULT_TYPES.map((type) => {
                    const meta = RESULT_META[type];
                    const pct = weights[type] || 0;
                    return (
                      <div key={type} data-testid={`weight-${type}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-semibold text-slate-800">
                            {meta.emoji} {meta.name}
                          </span>
                          <span
                            className="text-sm font-bold text-slate-700 tabular-nums"
                            data-testid={`weight-pct-${type}`}
                          >
                            {pct}%
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700 ease-out"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: meta.accentHex,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Iteration buttons */}
              <div
                className="bg-white dark:bg-slate-900 border border-slate-100 rounded-2xl p-6 shadow-[0_2px_20px_-12px_rgba(15,23,42,0.1)]"
                data-testid="iteration-panel"
              >
                <h2 className="text-sm font-bold uppercase tracking-widest text-slate-600">
                  Nachjustieren
                </h2>
                <div className="mt-4 space-y-2.5">
                  {RESULT_TYPES.map((type) => {
                    const meta = RESULT_META[type];
                    const busy = iteratingType === type;
                    const disabled = iteratingType !== null;
                    return (
                      <Button
                        key={type}
                        onClick={() => handleIterate(type)}
                        disabled={disabled}
                        data-testid={`iterate-btn-${type}`}
                        className={`w-full rounded-xl font-semibold h-11 shadow-sm hover:-translate-y-0.5 transition-all duration-200 ${meta.btnClass} disabled:opacity-60 disabled:hover:translate-y-0`}
                      >
                        <span className="mr-2">{meta.emoji}</span>
                        {meta.iterateLabel}
                        {busy && (
                          <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                        )}
                      </Button>
                    );
                  })}
                </div>

                <div className="mt-5 pt-5 border-t border-slate-100">
                  <Button
                    onClick={handleCopy}
                    data-testid="copy-message-btn"
                    className="w-full rounded-xl bg-[#1E40AF] hover:bg-blue-900 text-white font-semibold h-12 shadow-sm hover:-translate-y-0.5 transition-all duration-200"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Kopiert
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Kopieren
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
